import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Paper,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { FaEye, FaPencilAlt, FaDollarSign } from "react-icons/fa";
import {Can} from "../../Can";
import {ability} from "../../../config/acl/ability";

const DataTable = ({
                     data,
                     allData,
                     selected,
                     selectAllMode,
                     onSelectionChange,
                     totalCount,
                     page,
                     rowsPerPage,
                     onAddBalance,
                     sortConfig,
                     onSort,
                   }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const canAddBalance = ability.can('users', 'addBalance');

  // Safe function to get column display name
  const getColumnDisplayName = (column) => {
    if (typeof column !== 'string') return 'Column';

    switch (column) {
      case "email_verified_at":
        return "Verify at";
      case "panels":
        return "Panels";
      default:
        return column.charAt(0).toUpperCase() + column.slice(1);
    }
  };

  const handleSelect = (event, id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange(newSelected, selectAllMode);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Enter select-all mode
      onSelectionChange(new Set(), true);
    } else {
      // Exit select-all mode
      onSelectionChange(new Set(), false);
    }
  };

  const isSelected = (id) => {
    if (selectAllMode) {
      return !selected.has(id); // In select-all mode, selected Set contains EXCLUDED items
    }
    return selected.has(id);
  };

  const isHeaderCheckboxIndeterminate = () => {
    return !selectAllMode && selected.size > 0 && selected.size < data.length;
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getSortDirection = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction;
    }
    return undefined; // Return undefined instead of false for inactive columns
  };

  // Define columns with proper string values
  const columns = ["id", "name", "email", "panels", "reseller", "balance", "status", "email_verified_at"];

  return (
      <>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow className="bg-primary">
                <TableCell padding="checkbox">
                  <Checkbox
                      color="primary"
                      indeterminate={isHeaderCheckboxIndeterminate()}
                      checked={selectAllMode || selected.size > 0}
                      onChange={handleSelectAll}
                  />
                </TableCell>
                {columns.map((column) => (
                    <TableCell
                        key={column}
                        align="center"
                        className="!text-darkTextColor whitespace-nowrap"
                    >
                      <TableSortLabel
                          active={sortConfig.field === column}
                          direction={getSortDirection(column)}
                          onClick={() => onSort(column)}
                      >
                        {getColumnDisplayName(column)}
                      </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell
                    align="center"
                    className="!text-darkTextColor whitespace-nowrap"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.map ? data.map((item) => {
                const isItemSelected = isSelected(item.id);
                return (
                    <TableRow
                        key={item.id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        hover
                        selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={(event) => handleSelect(event, item.id)}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row" align="center">
                        {item?.id || 'N/A'}
                      </TableCell>
                      <TableCell align="center">{item?.name || 'N/A'}</TableCell>
                      <TableCell align="center">{item?.email || 'N/A'}</TableCell>
                      <TableCell align="center">
                        {item?.panels?.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                              {item.panels.map((panelUrl, index) => (
                                  <span
                                      key={index}
                                      style={{
                                        backgroundColor: '#f0f0f0',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        wordBreak: 'break-all',
                                        maxWidth: '200px'
                                      }}
                                  >
                                    {panelUrl || 'N/A'}
                                  </span>
                              ))}
                            </div>
                        ) : (
                            'No panels'
                        )}
                      </TableCell>
                      <TableCell align="center">{item?.reseller ?? "Not assign to reseller"}</TableCell>
                      <TableCell align="center">${item?.balance || '0.00'}</TableCell>
                      <TableCell align="center">
                    <span
                        className={`badge w-24 text-darkTextColor ${
                            item?.active === 1
                                ? "bg-[#28a745]"
                                : "bg-[#ff6877]"
                        }`}
                    >
                      {item?.active === 1 ? "Active" : "Not active"}
                    </span>
                      </TableCell>
                      <TableCell align="center">
                    <span
                        className={`badge w-24 text-darkTextColor ${
                            item?.email_verified_at !== "not verified"
                                ? "bg-[#28a745]"
                                : "bg-[#ff6877]"
                        }`}
                    >
                      {item?.email_verified_at || 'not verified'}
                    </span>
                      </TableCell>
                      <TableCell align="center">
                        <div className="flex gap-x-4 text-lg items-center justify-center">
                          <Link
                              to={`/users/Edit/${item?.id}`}
                              color="inherit"
                              underline="none"
                          >
                            <FaPencilAlt className="text-[#6c757d]" />
                          </Link>
                          <Link
                              to={`/users/${item?.id}`}
                              color="inherit"
                              underline="none"
                              className="flex items-center justify-center"
                          >
                            <FaEye className="text-[#6c757d]" />
                          </Link>
                          {
                            canAddBalance &&
                            <Tooltip title="Add Balance">
                              <IconButton
                                  onClick={() => onAddBalance(item)}
                                  size="small"
                              >
                                <FaDollarSign className="text-[#28a745]" />
                              </IconButton>
                            </Tooltip>
                          }

                        </div>
                      </TableCell>
                    </TableRow>
                );
              }) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </>
  );
};

export default DataTable;